import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { listInvoices } from '@/lib/billing/api';
import { daysInPeriod, formatDay, formatMinor, formatPeriod, formatRateBp } from '@/lib/billing/format';
import type { Invoice, InvoiceLine, InvoiceStatus } from '@/lib/billing/types';

// /billing — the invoices Nexa issued to this airline (commercial-contract M2,
// docs/verticals/commercial-contract.md §6.10). Gated by `billing.view` at the route.
// Read-only: Nexa staff compose, issue and record payments in the admin console.

export function BillingRoute() {
  const { t } = useTranslation('admin');
  const list = useQuery({
    queryKey: ['billing', 'invoices'] as const,
    queryFn: listInvoices,
    staleTime: 60_000,
  });

  return (
    <main className="density-page mx-auto flex max-w-4xl flex-col gap-4 p-4 sm:p-6">
      <header>
        <h1 className="text-2xl font-semibold">{t('common:nav.billing')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('billing.subtitle')}</p>
      </header>

      {list.isLoading ? (
        <Skeleton className="h-24 w-full" data-testid="invoices-loading" />
      ) : list.isError ? (
        <Card>
          <CardContent className="p-6 text-sm text-destructive" role="alert">
            {t('billing.loadError')}
          </CardContent>
        </Card>
      ) : (list.data ?? []).length === 0 ? (
        <EmptyState title={t('billing.empty.title')} description={t('billing.empty.description')} />
      ) : (
        <ul className="space-y-2" data-testid="invoices-list">
          {(list.data ?? []).map((inv) => (
            <li key={inv.invoiceUrn}>
              <InvoiceCard invoice={inv} />
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}

function InvoiceCard({ invoice }: { invoice: Invoice }) {
  const { t, i18n } = useTranslation('admin');
  const [open, setOpen] = useState(false);
  const lang = i18n.language;
  const detailId = `invoice-lines-${invoice.invoiceUrn}`;

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-medium">{formatPeriod(invoice.periodStart, lang)}</p>
              <StatusPill status={invoice.status} />
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              <span className="font-mono">{invoice.number}</span>
              {' · '}
              {t('billing.issuedDue', { issued: formatDay(invoice.issuedAt, lang), due: formatDay(invoice.dueAt, lang) })}
              {invoice.paidAt ? t('billing.paidSuffix', { paid: formatDay(invoice.paidAt, lang) }) : ''}
            </p>
          </div>
          <p className="text-lg font-semibold tabular-nums sm:text-right">
            {formatMinor(invoice.totalMinor, invoice.currency, lang)}
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2"
            aria-expanded={open}
            aria-controls={detailId}
            onClick={() => setOpen((v) => !v)}
          >
            <ChevronDown className={`mr-1 h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden />
            {open ? t('billing.hideDetails') : t('billing.details')}
          </Button>
          {/* The same invoice by disruption, each one down to its lines (billing-reports BU1). */}
          {invoice.status === 'void' ? null : (
            <Link
              to={`/billing/invoices/${encodeURIComponent(invoice.invoiceUrn)}`}
              className="inline-flex items-center gap-1 text-sm font-medium text-nexa-blue underline-offset-2 hover:underline dark:text-sky-300"
              data-testid="invoice-by-disruption"
            >
              {t('billing:list.byDisruption')}
              <ChevronRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
          )}
        </div>

        {open ? (
          <ul id={detailId} className="divide-y rounded-md border text-sm" data-testid="invoice-lines">
            {invoice.lines.map((line, i) => (
              <li key={i} className="flex items-start justify-between gap-4 px-3 py-2">
                <span className="min-w-0">{describeLine(line, invoice, t, lang)}</span>
                <span className={`shrink-0 tabular-nums ${line.amountMinor < 0 ? 'text-emerald-700 dark:text-emerald-400' : ''}`}>
                  {formatMinor(line.amountMinor, invoice.currency, lang)}
                </span>
              </li>
            ))}
            <li className="flex items-center justify-between gap-4 bg-muted/40 px-3 py-2 font-medium">
              <span>{t('billing.total')}</span>
              <span className="tabular-nums">{formatMinor(invoice.totalMinor, invoice.currency, lang)}</span>
            </li>
          </ul>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** One invoice line in words — prices only; a RESALE line never shows a rate. */
function describeLine(line: InvoiceLine, invoice: Invoice, t: TFunction, lang: string): string {
  const service = line.serviceType ? t(`billing.service.${line.serviceType}`) : '';
  const money = (minor: number | null) => (minor === null ? '—' : formatMinor(minor, invoice.currency, lang));
  switch (line.kind) {
    case 'SERVICE':
      return t('billing.line.service', { service, count: line.quantity });
    case 'SERVICE_FEE':
      return t('billing.line.serviceFee', {
        service,
        rate: line.rateBp === null ? '—' : formatRateBp(line.rateBp, lang),
        base: money(line.baseMinor),
        count: line.quantity,
      });
    case 'DISRUPTION_FEE':
      return t(line.unit === 'PASSENGER' ? 'billing.line.disruptionFeePassenger' : 'billing.line.disruptionFeePnr', {
        count: line.quantity,
        unit: money(line.unitMinor),
      });
    case 'MONTHLY_FEE': {
      const days = daysInPeriod(invoice.periodStart, invoice.periodEnd);
      return line.quantity < days
        ? t('billing.line.monthlyFeeProrated', { days: line.quantity, total: days, fee: money(line.unitMinor) })
        : t('billing.line.monthlyFee');
    }
    case 'CREDIT':
      return t('billing.line.credit', { what: service || t('billing.line.disruptionFeeShort'), count: line.quantity });
    default:
      return '';
  }
}

function StatusPill({ status }: { status: InvoiceStatus }) {
  const { t } = useTranslation('admin');
  const cls =
    status === 'paid'
      ? 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100'
      : status === 'overdue'
        ? 'bg-destructive/10 text-destructive'
        : status === 'void'
          ? 'bg-muted text-muted-foreground line-through'
          : 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100';
  return <span className={`rounded px-1.5 py-0.5 text-xs ${cls}`}>{t(`billing.status.${status}`)}</span>;
}
